import {
  type Page_projectsUsernameQuery,
  type Page_projectsUsernameQuery$data,
} from '#app/__generated__/relay/Page_projectsUsernameQuery.graphql.js';
import { useProfileMagic } from '#app/components/ContraMagic/useProfileMagic.js';
import { Footer } from '#app/components/Footer/Footer.js';
import { NotFound } from '#app/components/NotFound/NotFound.js';
import { PortfolioRedirect } from '#app/components/PortfolioRedirect/PortfolioRedirect.js';
import { Redirect } from '#app/components/Redirect/index.js';
import { useOnboardingRedirection } from '#app/components/RedirectToOnboardingModal/useOnboardingRedirection.js';
import { SSRSafeRender } from '#app/components/SSRSafeRender/SSRSafeRender.js';
import { ProfileProjects } from '#app/features/profile/components/ProfileProjects/ProfileProjects.js';
import { ProfileLayout } from '#app/features/profile/layouts/ProfileLayout.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useHasOnboarded } from '#app/hooks/useHasOnboarded.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useRegisterAnalyticEvent } from '#app/hooks/useRegisterAnalyticEvent.js';
import { useRouter } from '#app/hooks/useRouter.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { type PageProps } from '#app/types/router.js';
import { removeSearchParameter } from '#app/utilities/removeSearchParameter.js';
import { useMount } from '@contra/react-hooks/useMount';
import { graphql, usePreloadedQuery } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const AuthenticatedVisitorRedirect = ({
  userProfileByUsername,
  visitor,
}: {
  readonly userProfileByUsername: Page_projectsUsernameQuery$data['userProfileByUsername'];
  readonly visitor: Page_projectsUsernameQuery$data['visitor'];
}) => {
  const navigation = useNavigation();
  const location = useLocation();
  const { isContractor } = useSelectedUserType();
  const { hasOnboardedAsIndependent } = useHasOnboarded();
  const router = useRouter();

  const {
    hasSeenProfileMagicModal,
    hasUsedProfileMagic,
    isProfileMagicInputModalOpen,
    openProfileMagicModal,
  } = useProfileMagic({
    entrypoint: 'introModal',
    prefilledReason:
      visitor?.userAccount?.contraMagicQuestionnaireResponse
        ?.whatMakesYouUnique,
    prefilledServices:
      visitor?.userAccount?.contraMagicQuestionnaireResponse
        ?.whatServicesDoYouOffer,
    visitorCanEdit: userProfileByUsername?.visitorCanEdit ?? false,
  });

  const { handleOnboardingRedirectionModalOpen, shouldRedirectUser } =
    useOnboardingRedirection({
      redirectTo: userProfileByUsername
        ? getRoute('/@username', {
            args: {
              username: userProfileByUsername.displayUsername,
            },
          })
        : getRoute('/me'),
    });

  useMount(() => {
    if (shouldRedirectUser && userProfileByUsername?.visitorCanEdit) {
      handleOnboardingRedirectionModalOpen();
    } else if (
      !hasSeenProfileMagicModal &&
      !hasUsedProfileMagic &&
      userProfileByUsername?.visitorCanEdit &&
      isContractor &&
      hasOnboardedAsIndependent
    ) {
      openProfileMagicModal();
    }
  });

  const [urlSearchParameters] = useURLParameters('/@username/projects');

  const redirectToPortfolio = urlSearchParameters.redirectToPortfolio;
  const profileMagicParameter = urlSearchParameters.profileMagic;

  useEffect(() => {
    if (profileMagicParameter && !isProfileMagicInputModalOpen) {
      openProfileMagicModal();
      navigation.replace(removeSearchParameter(location.path, 'profileMagic'));
    }
  }, [profileMagicParameter]);

  if (redirectToPortfolio && userProfileByUsername) {
    return (
      <PortfolioRedirect
        displayUsername={userProfileByUsername.displayUsername}
        route={
          router.isActive(
            getRoute('/@username/projects', {
              args: {
                username: userProfileByUsername.displayUsername ?? '',
              },
            }),
            false,
          )
            ? 'userProjects'
            : 'user'
        }
      />
    );
  }

  return null;
};

type ProfileProjectsPageProps = PageProps<
  '/@username',
  Page_projectsUsernameQuery
> & {
  readonly preloaded: { isFilteringRoute?: boolean };
};

const HybridPage = ({ preloaded }: ProfileProjectsPageProps) => {
  const data = usePreloadedQuery<Page_projectsUsernameQuery>(
    graphql`
      query Page_projectsUsernameQuery($username: String!) {
        visitor {
          id
          userAccount {
            id
            contraMagicQuestionnaireResponse {
              whatMakesYouUnique
              whatServicesDoYouOffer
            }
            ...ProfileLayout_userAccount
          }
        }
        userProfileByUsername(username: $username) {
          id
          profileDisplayType
          id
          firstName
          lastName
          visitorCanEdit
          displayUsername
          hasCompletedClientOnboarding
          hasCompletedIndyOnboarding
          ...ProfileLayout_userProfile
          ...ProfileProjects_userProfile
        }

        blogCategories {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    `,
    preloaded,
  );

  const { userProfileByUsername, visitor } = data;

  const isBlogProfile = userProfileByUsername?.profileDisplayType === 'BLOG';

  const blogCategories = data.blogCategories?.edges.map(
    ({ node: { id: categoryId, name } }) => ({ id: categoryId, name }),
  );

  useRegisterAnalyticEvent(
    userProfileByUsername && !userProfileByUsername.visitorCanEdit
      ? {
          eventType: 'UserProfileView',
          input: { userProfileId: userProfileByUsername.id },
        }
      : undefined,
  );

  if (!userProfileByUsername) {
    return <NotFound />;
  }

  if (
    userProfileByUsername.hasCompletedClientOnboarding &&
    !userProfileByUsername.hasCompletedIndyOnboarding
  ) {
    return (
      <Redirect
        method="replace"
        to={getRoute('/@username/opportunities', {
          args: {
            username: userProfileByUsername.displayUsername,
          },
        })}
      />
    );
  }

  /* Boop the user over the main profile page if this isn't a blog and they're trying to filter! */
  if (!isBlogProfile && preloaded.isFilteringRoute) {
    return (
      <Redirect
        method="replace"
        to={getRoute('/@username', {
          args: {
            username: userProfileByUsername.displayUsername,
          },
        })}
      />
    );
  }

  return (
    <>
      <Suspense>
        {visitor.userAccount ? (
          <AuthenticatedVisitorRedirect
            userProfileByUsername={userProfileByUsername}
            visitor={visitor}
          />
        ) : null}
      </Suspense>

      <Helmet>
        <title>{`${isBlogProfile ? 'Articles' : 'Projects created'} by ${
          userProfileByUsername.firstName
        } ${userProfileByUsername.lastName}`}</title>
      </Helmet>

      <ProfileLayout
        accountRef={visitor.userAccount}
        userProfileRef={userProfileByUsername}
      >
        <ProfileProjects
          blogCategories={blogCategories}
          profileKey={userProfileByUsername}
        />
      </ProfileLayout>

      <Footer />
    </>
  );
};

export const Page = ({ preloaded }: ProfileProjectsPageProps) => {
  const currentUser = useCurrentUser();

  return (
    <SSRSafeRender skipServerRender={Boolean(currentUser)}>
      <HybridPage preloaded={preloaded} />
    </SSRSafeRender>
  );
};
